import React, { useCallback, useState } from "react";
import { ReferenceLine } from "interfaces/chart";
import { MenuTooltip } from "components/DataExplorationV2/MenuTooltip";
import {
  IconButton,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  TextField
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { styled } from "@mui/material/styles";

export interface ReferenceLinesProps {
  loading: boolean;
  error: boolean;
  referenceLines: ReferenceLine[];
  selectedReferenceLine: ReferenceLine | null;
  onChangeSelectedReferenceLine: (referenceLine: ReferenceLine | null) => void;
  onDeleteReferenceLine: (referenceLineId: string) => void;
  onChangeReferenceLineTitle: (
    referenceLine: ReferenceLine,
    title: string | null
  ) => void;
}

export const ReferenceLines = ({
  loading,
  error,
  referenceLines,
  selectedReferenceLine,
  onChangeSelectedReferenceLine,
  onDeleteReferenceLine,
  onChangeReferenceLineTitle
}: ReferenceLinesProps): JSX.Element => {
  const [
    editTargetReference,
    setEditTargetReference
  ] = useState<ReferenceLine | null>(null);
  const [editTitle, setEditTitle] = useState<string | null>(null);

  const onEditReferenceLine = useCallback(
    (el: React.SetStateAction<any | null>) => {
      setEditTargetReference(el);
      setEditTitle(el?.title);
    },
    []
  );

  const onSubmitEditTitle = useCallback(() => {
    if (editTargetReference && editTitle !== editTargetReference?.title) {
      onChangeReferenceLineTitle(editTargetReference, editTitle);
    }
    setEditTargetReference(null);
    setEditTitle(null);
  }, [editTargetReference, editTitle, onChangeReferenceLineTitle]);

  const onCancelEditTitle = useCallback(() => {
    setEditTargetReference(null);
    setEditTitle(null);
  }, []);

  return (
    <div>
      {referenceLines && referenceLines?.length > 0 ? (
        <StyledList>
          {referenceLines.map((el: any, index: number) => {
            /** Title Edit row */
            if (editTargetReference && editTargetReference?.id === el.id) {
              return (
                <EditListItem key={`Reference_edit_${index}`}>
                  <EditTitleInput
                    variant="outlined"
                    size="small"
                    color="secondary"
                    value={editTitle}
                    fullWidth
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setEditTitle(event.target.value)
                    }
                  />

                  <MenuTooltip title="Confirm edit">
                    <SecondaryButton
                      aria-label="Confirm edit title"
                      onClick={onSubmitEditTitle}
                    >
                      <CheckIcon fontSize="small" color="secondary" />
                    </SecondaryButton>
                  </MenuTooltip>

                  <MenuTooltip title="Cancel edit title">
                    <SecondaryButton
                      aria-label="Cancel editing reference line title"
                      onClick={onCancelEditTitle}
                    >
                      <CloseIcon fontSize="small" color="secondary" />
                    </SecondaryButton>
                  </MenuTooltip>
                </EditListItem>
              );
            } else {
              /** Static row */
              return (
                <StyledListItemButton
                  key={`Reference_${index}`}
                  aria-label="Reference line row"
                  disabled={loading || error}
                  selected={el.id === selectedReferenceLine?.id}
                  onClick={() => onChangeSelectedReferenceLine(el)}
                >
                  <MenuTooltip title={el.title}>
                    <RowTitle>{el.title}</RowTitle>
                  </MenuTooltip>
                  <ListItemSecondaryAction>
                    <MenuTooltip title="Edit title">
                      <SecondaryButton
                        aria-label="Edit reference line title"
                        onClick={() => onEditReferenceLine(el)}
                      >
                        <EditIcon fontSize="small" color="secondary" />
                      </SecondaryButton>
                    </MenuTooltip>

                    <MenuTooltip title="Delete">
                      <SecondaryButton
                        aria-label="Delete reference line"
                        onClick={() => onDeleteReferenceLine(el.id)}
                      >
                        <DeleteIcon fontSize="small" color="secondary" />
                      </SecondaryButton>
                    </MenuTooltip>
                  </ListItemSecondaryAction>
                </StyledListItemButton>
              );
            }
          })}
        </StyledList>
      ) : null}
    </div>
  );
};

const SecondaryButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  padding: 0 !important;
  .MuiSvgIcon-root {
    transition: all 150ms ease-out;
    &:hover {
      color: #19173f !important;
      opacity: 0.8;
    }
  }
`;

const StyledList = styled(List)`
  font-size: 0.75rem;
  margin: 0 -${({ theme }) => theme.spacing(2)} !important;
  padding: 0 ${({ theme }) => theme.spacing(2)};
`;

const StyledListItemButton = styled(ListItemButton)`
  height: 36px;
  padding-right: 60px;
  &.MuiListItemButton-root:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  &.MuiListItemButton-root.Mui-selected {
    background-color: rgba(0, 0, 0, 0.1);
  }
  &.MuiListItemButton-root.Mui-selected:hover {
    background-color: rgba(0, 0, 0, 0.15);
  }
`;

const EditListItem = styled("div")`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)}
    ${props => props.theme.spacing(1)} 0;
`;

const EditTitleInput = styled(TextField)`
  padding-right: ${props => props.theme.spacing(1)};
`;

const RowTitle = styled("div")`
  word-break: break-word;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
